import React, { useMemo } from 'react';
import { Box, Autocomplete, TextField, Checkbox } from '@mui/material';
import { DataGrid, GridColDef, GridActionsCellItem } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import SwitchAccessShortcutAddIcon from '@mui/icons-material/SwitchAccessShortcutAdd';
import { TimeInput } from '../common';
import { AssignmentState, AssignmentHandlers, WorkRule } from '../common/types';

interface AssignmentRulesTabProps {
    state: AssignmentState;
    handlers: AssignmentHandlers;
    workRules: WorkRule[];
    mode: string;
}

export const AssignmentRulesTab: React.FC<AssignmentRulesTabProps> = ({ state, handlers, workRules, mode }) => {
    const { t } = useTranslation();

    const days_week = useMemo(
        () => [
            { key: 'monday', label: t('general.days_week.monday') },
            { key: 'tuesday', label: t('general.days_week.tuesday') },
            { key: 'wednesday', label: t('general.days_week.wednesday') },
            { key: 'thursday', label: t('general.days_week.thursday') },
            { key: 'friday', label: t('general.days_week.friday') },
            { key: 'saturday', label: t('general.days_week.saturday') },
            { key: 'sunday', label: t('general.days_week.sunday') },
        ],
        [t],
    );

    const columnsAssignment: GridColDef[] = useMemo(
        () => [
            {
                field: 'actions',
                type: 'actions',
                flex: 1,
                headerAlign: 'center',
                align: 'center',
                getActions: (params) => {
                    const rowIndex = state.rows.findIndex((row) => row.id === params.row.id);
                    const isFirstRow = rowIndex === 0;
                    const isLastRow = rowIndex === state.rows.length - 1;
                    const isOnlyRow = state.rows.length === 1;

                    const actions = [];

                    if (!isFirstRow && !isOnlyRow) {
                        actions.push(
                            <GridActionsCellItem key="up" disabled={mode === 'info'} icon={<ArrowUpwardIcon />} label="" onClick={() => handlers.moveRowUp(params.row.id)} showInMenu={false} />,
                        );
                    }

                    if (!isLastRow && !isOnlyRow) {
                        actions.push(
                            <GridActionsCellItem key="down" disabled={mode === 'info'} icon={<ArrowDownwardIcon />} label="" onClick={() => handlers.moveRowDown(params.row.id)} showInMenu={false} />,
                        );
                    }

                    if (!isOnlyRow) {
                        actions.push(
                            <GridActionsCellItem key="delete" disabled={mode === 'info'} icon={<DeleteIcon />} label="" onClick={() => handlers.deleteRow(params.row.id)} showInMenu={false} />,
                        );
                    }

                    actions.push(
                        <GridActionsCellItem
                            key="add"
                            disabled={mode === 'info'}
                            icon={<SwitchAccessShortcutAddIcon sx={{ transform: 'rotate(90deg)' }} />}
                            label=""
                            onClick={handlers.addRow}
                            showInMenu={false}
                        />,
                    );

                    return actions;
                },
                resizable: false,
                filterable: false,
                sortable: false,
                editable: false,
                groupable: false,
            },
            {
                field: 'work_rule',
                headerName: t('settings.pay_rules.dialog.fields.work_rule'),
                flex: 1.6,
                align: 'center',
                renderCell: (params) => (
                    <Autocomplete
                        size="small"
                        sx={{ mt: 0.5 }}
                        fullWidth
                        options={workRules}
                        value={workRules.find((opt) => opt.work_rule_id === params.row.work_rule_id) || null}
                        isOptionEqualToValue={(option, value) => option?.work_rule_id === value?.work_rule_id}
                        getOptionLabel={(option) => option.name || ''}
                        blurOnSelect
                        disabled={state.loading || mode === 'info'}
                        loading={state.loading}
                        onChange={(e, newValue) => handlers.updateRow(params.row.id, 'work_rule_id', newValue?.work_rule_id || null)}
                        renderInput={(params) => <TextField {...params} label={'Seleccione opción'} />}
                    />
                ),
                resizable: false,
                filterable: false,
                sortable: false,
                editable: false,
                groupable: false,
            },
            {
                field: 'start_hour',
                headerName: t('settings.pay_rules.dialog.fields.tabs.table2.start_hour'),
                flex: 0.6,
                align: 'center',
                renderCell: (params) => <TimeInput value={params.row.start_hour || ''} rowId={params.row.id} field="start_hour" onUpdate={handlers.updateRow} mode={mode} />,
                resizable: false,
                filterable: false,
                sortable: false,
                editable: false,
                groupable: false,
            },
            {
                field: 'end_hour',
                headerName: t('settings.pay_rules.dialog.fields.tabs.table2.end_hour'),
                flex: 0.6,
                align: 'center',
                renderCell: (params) => <TimeInput value={params.row.end_hour || ''} rowId={params.row.id} field="end_hour" onUpdate={handlers.updateRow} mode={mode} />,
                resizable: false,
                filterable: false,
                sortable: false,
                editable: false,
                groupable: false,
            },
            {
                field: 'min_shift',
                headerName: t('settings.pay_rules.dialog.fields.tabs.table2.min_shift'),
                flex: 0.6,
                align: 'center',
                renderCell: (params) => <TimeInput value={params.row.min_shift || ''} rowId={params.row.id} field="min_shift" onUpdate={handlers.updateRow} mode={mode} />,
                resizable: false,
                filterable: false,
                sortable: false,
                editable: false,
                groupable: false,
            },
            {
                field: 'max_shift',
                headerName: t('settings.pay_rules.dialog.fields.tabs.table2.max_shift'),
                flex: 0.6,
                align: 'center',
                renderCell: (params) => <TimeInput value={params.row.max_shift || ''} rowId={params.row.id} field="max_shift" onUpdate={handlers.updateRow} mode={mode} />,
                resizable: false,
                filterable: false,
                sortable: false,
                editable: false,
                groupable: false,
            },
            ...days_week.map((day) => ({
                field: day.key,
                description: day.label,
                headerName: day.label.charAt(0).toUpperCase(),
                headerAlign: 'center' as const,
                flex: 0.4,
                align: 'center' as const,
                renderCell: (params: { row: { id: number; days_week: Set<string> } }) => (
                    <Checkbox
                        color="success"
                        size="small"
                        checked={params.row.days_week.has(day.key)}
                        onChange={(e) =>
                            handlers.updateRow(
                                params.row.id,
                                'days_week',
                                e.target.checked ? new Set([...params.row.days_week, day.key]) : new Set([...params.row.days_week].filter((d) => d !== day.key)),
                            )
                        }
                        disabled={mode === 'info'}
                    />
                ),
                resizable: false,
                filterable: false,
                sortable: false,
                editable: false,
                groupable: false,
            })),
            {
                field: 'Planificado',
                headerName: t('settings.pay_rules.dialog.fields.tabs.table2.planned'),
                headerAlign: 'center',
                flex: 0.4,
                align: 'center',
                renderCell: (params) => (
                    <Checkbox size="small" checked={params.row.planned} onChange={(e) => handlers.updateRow(params.row.id, 'planned', e.target.checked)} disabled={mode === 'info'} />
                ),
                resizable: false,
                filterable: false,
                sortable: false,
                editable: false,
                groupable: false,
            },
            {
                field: 'NoPlanificado',
                headerName: t('settings.pay_rules.dialog.fields.tabs.table2.unplanned'),
                headerAlign: 'center',
                flex: 0.4,
                align: 'center',
                renderCell: (params) => (
                    <Checkbox size="small" checked={params.row.unplanned} onChange={(e) => handlers.updateRow(params.row.id, 'unplanned', e.target.checked)} disabled={mode === 'info'} />
                ),
                resizable: false,
                filterable: false,
                sortable: false,
                editable: false,
                groupable: false,
            },
        ],
        [workRules, handlers, state.rows, state.loading, days_week, t, mode],
    );

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: 450, mt: 1.5 }}>
            <DataGrid
                rows={state.rows}
                columns={columnsAssignment}
                density="standard"
                disableColumnMenu
                disableRowSelectionOnClick
                getRowId={(row) => row.id}
                hideFooter
                hideFooterPagination
                showCellVerticalBorder
                showColumnVerticalBorder
                sx={{
                    '& .MuiDataGrid-cell': {
                        cursor: 'pointer',
                    },
                    '& .MuiDataGrid-columnHeaderTitle': {
                        fontSize: '0.875rem',
                        fontWeight: 'bold',
                        whiteSpace: 'normal',
                        lineHeight: 1.2,
                        textAlign: 'center',
                        height: 'auto',
                    },
                    '& .MuiDataGrid-row': {
                        transition: 'all 0.3s ease-in-out',
                    },
                }}
                hideFooterSelectedRowCount
                autosizeOptions={{ outliersFactor: 0.5 }}
            />
        </Box>
    );
};
